import { SplitConfigSchema } from "@common/schemas/rule.schema";
import {
    getRuleEntityIds,
    resolveRuleTargetEntityId,
} from "@common/helpers/rules";
import {
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
    RuleActionType,
    TransactionRule,
    TransactionRuleAction,
    TransactionRuleGroup,
} from "@common/types/transactionRule";

export interface AccountRuleReference {
    id: string;
    name: string;
    entityId: number;
    kickOriginalCode?: number | null;
}

interface StoredNameReference {
    name: string;
    originalCode: number | null;
    entityIds: Set<number> | null;
}

interface AccountRuleReferences {
    ids: Set<string>;
    storedNames: StoredNameReference[];
}

interface AccountHasEnabledTransactionRuleParams {
    account: AccountRuleReference;
    ruleGroups: TransactionRuleGroup[];
}

interface GetAccountIdsWithEnabledTransactionRulesParams {
    accounts: AccountRuleReference[];
    ruleGroups: TransactionRuleGroup[];
}

function addStoredName(
    storedName: string,
    entityIds: Set<number> | null,
    references: AccountRuleReferences,
): void {
    const originalCode = storedName.includes(" - ")
        ? Number(storedName.split(" - ")[0])
        : NaN;
    references.storedNames.push({
        name: storedName,
        originalCode: Number.isNaN(originalCode) ? null : originalCode,
        entityIds,
    });
}

function addSplitReferences(
    action: TransactionRuleAction,
    references: AccountRuleReferences,
): void {
    if (
        action.actionType !== RuleActionType.SPLIT ||
        typeof action.value !== "string"
    ) {
        return;
    }

    try {
        const result = SplitConfigSchema.innerType().safeParse(
            JSON.parse(action.value),
        );
        if (!result.success) {
            return;
        }
        result.data.splits.forEach((split) => {
            if (split.accountId) {
                references.ids.add(split.accountId);
            }
            if (split.accrualAccountId) {
                references.ids.add(split.accrualAccountId);
            }
        });
    } catch {
        return;
    }
}

function addActionReferences(
    action: TransactionRuleAction,
    entityIds: Set<number> | null,
    references: AccountRuleReferences,
): void {
    switch (action.actionType) {
        case RuleActionType.ACCOUNT_ID: {
            const accountIds = parseAccountIdActionValue(action.value) ?? [];
            accountIds.forEach((accountId) => references.ids.add(accountId));
            return;
        }
        case RuleActionType.ACCOUNT_NAME:
            if (typeof action.value === "string") {
                addStoredName(action.value, entityIds, references);
            }
            return;
        case RuleActionType.ACCRUAL_ACCOUNT_ID: {
            const accountIds =
                parseAccrualAccountActionValue(action.value).accountIds ?? [];
            accountIds.forEach((accountId) => references.ids.add(accountId));
            return;
        }
        case RuleActionType.ACCRUAL_ACCOUNT_NAME: {
            const accountName = parseAccrualAccountActionValue(
                action.value,
            ).accountName;
            if (accountName) {
                addStoredName(accountName, entityIds, references);
            }
            return;
        }
        case RuleActionType.SPLIT:
            addSplitReferences(action, references);
            return;
        default:
            return;
    }
}

function resolveRuleTargetEntityIds(rule: TransactionRule): Set<number> | null {
    const ruleEntityIds = getRuleEntityIds(rule);
    if (ruleEntityIds === null) {
        const targetEntityId = resolveRuleTargetEntityId({
            actions: rule.actions,
            transactionEntityId: NaN,
        });
        return Number.isNaN(targetEntityId) ? null : new Set([targetEntityId]);
    }
    return new Set(
        ruleEntityIds.map((transactionEntityId) =>
            resolveRuleTargetEntityId({
                actions: rule.actions,
                transactionEntityId,
            }),
        ),
    );
}

function collectAccountRuleReferences(
    ruleGroups: TransactionRuleGroup[],
): AccountRuleReferences {
    const references: AccountRuleReferences = {
        ids: new Set(),
        storedNames: [],
    };
    ruleGroups.forEach((group) =>
        group.rules?.forEach((rule) => {
            if (!rule.isDisabled) {
                const entityIds = resolveRuleTargetEntityIds(rule);
                rule.actions.forEach((action) =>
                    addActionReferences(action, entityIds, references),
                );
            }
        }),
    );
    return references;
}

function referencesAccount(
    references: AccountRuleReferences,
    account: AccountRuleReference,
): boolean {
    if (references.ids.has(account.id)) {
        return true;
    }
    return references.storedNames.some(
        (storedName) =>
            (storedName.entityIds === null ||
                storedName.entityIds.has(account.entityId)) &&
            (storedName.name === account.name ||
                (account.kickOriginalCode != null &&
                    storedName.originalCode === account.kickOriginalCode)),
    );
}

export function accountHasEnabledTransactionRule({
    account,
    ruleGroups,
}: AccountHasEnabledTransactionRuleParams): boolean {
    const references = collectAccountRuleReferences(ruleGroups);
    return referencesAccount(references, account);
}

export function getAccountIdsWithEnabledTransactionRules({
    accounts,
    ruleGroups,
}: GetAccountIdsWithEnabledTransactionRulesParams): Set<string> {
    const references = collectAccountRuleReferences(ruleGroups);
    return new Set(
        accounts
            .filter((account) => referencesAccount(references, account))
            .map((account) => account.id),
    );
}
